if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderInstance = false;
        this.layout = this.dataset.layout;
        this.transitionMode = this.dataset.transitionMode || 'fade';
        this.isHomeHero = this.dataset.homeHero === 'true';
        this.items = Number(this.dataset.items);

        this.selectedIndex = this.selectedIndex;
        this.preloadedHeroSlides = new Set();
      }

      connectedCallback() {
        this.initSlider();

        if (this.classList.contains('slideshow-height--adapt')) {
          this.calcSlideHeight();
        }
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      initSlider() {
        const isFadeTransition = this.transitionMode !== 'slide';
        const additionModules = [FoxTheme.Swiper.Autoplay];

        if (isFadeTransition) {
          additionModules.push(FoxTheme.Swiper.EffectFade);
        }

        this.sliderOptions = {
          slidesPerView: 1,
          spaceBetween: isFadeTransition ? 10 : 0,
          loop: true,
          speed: isFadeTransition ? 300 : this.isHomeHero ? 520 : 720,
          grabCursor: true,
          allowTouchMove: true,
          followFinger: true,
          threshold: isFadeTransition ? 2 : this.isHomeHero ? 3 : 2,
          shortSwipes: true,
          longSwipesMs: isFadeTransition ? 300 : this.isHomeHero ? 140 : 180,
          longSwipesRatio: isFadeTransition ? 0.5 : this.isHomeHero ? 0.12 : 0.16,
          resistanceRatio: isFadeTransition ? 0.85 : this.isHomeHero ? 0.35 : 0.45,
          touchRatio: this.isHomeHero ? 1.08 : 1,
          touchAngle: this.isHomeHero ? 35 : 45,
          lazyPreloadPrevNext: this.isHomeHero ? 1 : 0,
          effect: isFadeTransition ? 'fade' : 'slide',
          on: {
            init: this.handleAfterInit.bind(this),
            touchStart: this.handleHeroTouchStart.bind(this),
            touchEnd: this.handleHeroTouchEnd.bind(this),
            transitionEnd: this.handleHeroTransitionEnd.bind(this),
          },
          ...(isFadeTransition && {
            fadeEffect: {
              crossFade: true,
            },
          }),
        };

        if (this.sliderControls) {
          this.sliderOptions.pagination = {
            el: this.sliderControls.querySelector('.swiper-pagination'),
            clickable: true,
          };
        }

        switch (this.layout) {
          case 'centered':
            this.sliderOptions = {
              ...this.sliderOptions,
              centeredSlides: true,
              slidesPerView: 'auto',
              spaceBetween: 10,
              effect: 'slide',
            };
            break;
        }

        if (this.layout !== 'centered' && this.items === 1) {
          this.sliderOptions.loop = false;
          this.classList.remove('slideshow-controls-visible--yes');
        }

        const autoplayDelay = parseInt(this.dataset.autoplay);
        if (autoplayDelay > 0) {
          this.sliderOptions = {
            ...this.sliderOptions,
            autoplay: {
              delay: autoplayDelay,
              disableOnInteraction: false,
            },
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
        this.sliderInstance.init();

        this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));

        if (this.sliderInstance) {
          if (this.layout === 'centered' && !FoxTheme.config.mqlMobile) {
            const motionTextElements = Array.from(this.querySelectorAll('motion-element[data-text]'));
            motionTextElements &&
              motionTextElements.forEach((motionTextElement) => {
                if (
                  typeof motionTextElement.resetAnimation === 'function' &&
                  !motionTextElement.closest('.swiper-slide-active')
                ) {
                  motionTextElement.resetAnimation();
                }
              });
          }
          this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
          this.onReady(this.selectedElement, this.sliderInstance.slider.slides);
          this.scheduleHeroPreload(this.selectedElement);
          // Fix accessibility
          const focusableElements = FoxTheme.a11y.getFocusableElements(this);
          if (this.layout === 'centered') {
            focusableElements.forEach((element) => {
              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                const slideToIndex = this.sliderInstance.slider.slides.indexOf(slide);
                this.sliderInstance.slider.slideToLoop(slideToIndex);
              });
            });
          } else {
            focusableElements.forEach((element) => {
              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
              });
            });
          }
        }
      }

      onReady(selectedElement) {
        if (selectedElement.dataset.type === 'video') {
          const videoElement = FoxTheme.utils.displayedMedia(selectedElement.querySelectorAll('video-element'));
          videoElement?.play();
        }

        if (FoxTheme.config.motionReduced) {
          this.removeAttribute('data-media-loading');
        } else {
          const motionEls = selectedElement.querySelectorAll('motion-element');
          motionEls.forEach((motionEl) => {
            setTimeout(() => {
              motionEl && motionEl.removeAttribute('hold');
              if (this.transitionMode === 'slide' && motionEl.closest('.slideshow__slide-bg')) {
                this.removeAttribute('data-media-loading');
                motionEl.classList.add('is-animated');
                return;
              }
              motionEl && motionEl.preInitialize();
              this.removeAttribute('data-media-loading');
              motionEl.initialize();
            });
          });
        }
      }

      handleAfterInit() {
        // Fix active bullet not transition on the first time.
        if (this.sliderControls) {
          const activeBullet = this.sliderControls.querySelector('.swiper-pagination-bullet-active');

          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight; // Trigger reflow.
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }
        }
      }

      scheduleHeroPreload(activeSlide = null) {
        if (!this.isHomeHero || !this.sliderInstance?.slider) return;

        const run = () => {
          const preload = () => this.preloadHeroAdjacentSlides();
          if (window.requestIdleCallback) {
            window.requestIdleCallback(preload, { timeout: 900 });
          } else {
            setTimeout(preload, 250);
          }
        };
        const activeImage =
          activeSlide?.querySelector('.slideshow__slide-bg img') ||
          this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex]?.querySelector(
            '.slideshow__slide-bg img'
          );

        if (activeImage && !activeImage.complete) {
          activeImage.addEventListener('load', run, { once: true });
          return;
        }

        run();
      }

      preloadHeroAdjacentSlides() {
        const swiper = this.sliderInstance?.slider;
        if (!swiper || !swiper.slides?.length) return;

        const slideIndexes = [swiper.activeIndex + 1, swiper.activeIndex - 1];
        slideIndexes.forEach((slideIndex) => {
          const slide = swiper.slides[(slideIndex + swiper.slides.length) % swiper.slides.length];
          this.preloadHeroSlide(slide);
        });
      }

      preloadHeroSlide(slide) {
        if (!slide) return;

        const slideKey = slide.dataset.swiperSlideIndex || slide.dataset.index;
        if (slideKey && this.preloadedHeroSlides.has(slideKey)) return;

        const images = slide.querySelectorAll('.slideshow__slide-bg img');
        images.forEach((image) => {
          image.loading = 'eager';
          image.decoding = 'async';

          if ('fetchPriority' in image) {
            image.fetchPriority = 'low';
          }

          const markLoaded = () => {
            image.classList.add('loaded');
            image.closest('.media-wrapper')?.classList.remove('loading');
            image.closest('.media-wrapper')?.classList.add('loaded');
          };

          if (image.complete) {
            markLoaded();
            return;
          }

          image.addEventListener('load', () => {
            if (typeof image.decode === 'function') {
              image.decode().then(markLoaded).catch(markLoaded);
            } else {
              markLoaded();
            }
          }, { once: true });
        });

        if (slideKey) {
          this.preloadedHeroSlides.add(slideKey);
        }
      }

      handleHeroTouchStart(swiper) {
        if (!this.isHomeHero) return;

        this.classList.remove('slideshow-release--quick', 'slideshow-release--settle');
        this.heroTouchStart = {
          time: performance.now(),
          x: swiper.touches?.startX || 0,
        };
        swiper.params.speed = 520;
      }

      handleHeroTouchEnd(swiper) {
        if (!this.isHomeHero || !this.heroTouchStart) return;

        const distance = Math.abs((swiper.touches?.currentX || this.heroTouchStart.x) - this.heroTouchStart.x);
        const duration = Math.max(performance.now() - this.heroTouchStart.time, 1);
        const velocity = distance / duration;
        const likelyNextSlide = distance > Math.max(swiper.width * 0.12, 36);
        const quickRelease = likelyNextSlide && velocity > 0.55;

        this.classList.toggle('slideshow-release--quick', quickRelease);
        this.classList.toggle('slideshow-release--settle', likelyNextSlide && !quickRelease);
        swiper.params.speed = quickRelease ? 520 : likelyNextSlide ? 400 : 480;
      }

      handleHeroTransitionEnd(swiper) {
        if (!this.isHomeHero) return;

        this.classList.remove('slideshow-release--quick', 'slideshow-release--settle');
        swiper.params.speed = 520;
        this.scheduleHeroPreload(swiper.slides[swiper.activeIndex]);
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;

        this.updateControlsScheme(slides[activeIndex]);
        this.scheduleHeroPreload(slides[activeIndex]);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          const fromElements = this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`);
          const toElements = this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`);

          fromElements.forEach((fromElement) => {
            if (fromElement.dataset.type === 'video') {
              const videoElement = FoxTheme.utils.displayedMedia([fromElement.querySelector('video-element')]);
              videoElement && videoElement.pause();
            }

            const motionEls = fromElement.querySelectorAll('motion-element');
            motionEls &&
              motionEls.forEach((motionEl) => {
                if (motionEl.hasAttribute('data-text')) {
                  motionEl.resetAnimation();
                }
              });
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (toElement.classList.contains('swiper-slide-active')) {
                if (toElement.dataset.type === 'video') {
                  const videoElement = FoxTheme.utils.displayedMedia([toElement.querySelector('video-element')]);
                  videoElement && videoElement.play();
                }

                const motionEls = toElement.querySelectorAll('motion-element');
                motionEls.forEach((motionEl) => {
                  if (this.transitionMode === 'slide' && motionEl.closest('.slideshow__slide-bg')) {
                    motionEl.removeAttribute('hold');
                    motionEl.classList.add('is-animated');
                    return;
                  }
                  motionEl && motionEl.refreshAnimation();
                });
              }
            });
          });
        }
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls) {
          const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
            className.startsWith('color-')
          );
          classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
          const colorScheme = activeSlide.dataset.colorScheme;
          this.sliderControls.classList.add(colorScheme);
        }
      }

      calcSlideHeight() {
        let maxHeight = 0;
        const slides = this.querySelectorAll('.swiper-slide');
        slides &&
          slides.forEach((slide) => {
            const slideText = slide.querySelector('.slideshow__text');
            if (slideText) {
              const slideTextHeight = slideText.offsetHeight;
              if (slideTextHeight > maxHeight) {
                maxHeight = slideTextHeight;
              }
            }
          });

        if (this.classList.contains('slideshow-controls-position--bottom')) {
          maxHeight += 100; // Avoid overlap with dots.
        }

        this.style.setProperty('--slide-height', maxHeight + 'px');
      }
    }
  );
}
